import { useRef } from "react";
import {
  generatedV2,
  useV2CreateClientMutation,
  useV2CreateProjectLocationMutation,
  useV2CreateProjectMutation,
  useV2CreateSalesExecutiveMutation,
  useV2UpdateProjectLocationMutation,
  useV2UpdateProjectMutation,
} from "@generated";
import { formatUnprocessibleResponse } from "@utils/helperFunctions";
import { locationSaveFailedMessage } from "../../projectUtils/projectHelperFunctions";
import {
  ApiRecord,
  NewProjectLocation,
  NewProjectValues,
  ProjectSaveError,
} from "../types";

type SalesExec = { id: string | null; warning: string | null };

/**
 * The project write. A create leaves an empty relationship out; an update
 * (`projectId` given) sends `data: null`, which clears it.
 */
export const projectData = (
  values: NewProjectValues,
  clientId: string,
  salesExec: SalesExec,
  projectId?: string
) => {
  const toOne = (key: string, type: string, id: string | null) =>
    id
      ? { [key]: { data: { type, id } } }
      : projectId
      ? { [key]: { data: null } }
      : {};
  return {
    type: "projects",
    ...(projectId && { id: projectId }),
    attributes: {
      "project-name": values.projectName,
      "msa-date": values.msaDate,
      "project-variables": values.projectVariables,
      "tag-list": values.tags,
    },
    relationships: {
      ...toOne("client", "clients", clientId),
      // One that couldn't be added leaves the saved sales executive.
      ...(!salesExec.warning &&
        toOne("sales-executive", "sales-executives", salesExec.id)),
      ...toOne(
        "presales-engineer",
        "presales-engineers",
        values.presalesEngineerId
      ),
      ...toOne("payment-term", "payment-terms", values.paymentTermId),
      ...toOne("rate-table", "rate-tables", values.rateTableId),
      ...toOne("business-unit", "business-units", values.businessUnitId),
      // Removing an opportunity has its own endpoint.
      ...(values.crmOpportunityId &&
        toOne("crm-opportunity", "crm-opportunities", values.crmOpportunityId)),
    },
  };
};

const locationBody = (
  projectId: string,
  location: NewProjectLocation,
  locationId: string | null
) => ({
  type: "project-locations",
  ...(locationId && { id: locationId }),
  attributes: {
    name: location.name,
    street: location.street,
    street2: location.street2,
    city: location.city,
    state: location.state,
    "postal-code": location.postalCode,
    country: location.countryId,
    remote: location.remote,
    "project-variables": location.projectVariables,
    // crm-site-id is create-only: an update keeps the site the location has.
    ...(!locationId && location.link?.attributes),
  },
  relationships: {
    project: { data: { type: "projects", id: projectId } },
    ...location.link?.relationships,
  },
});

/**
 * Saves a ProjectForm's values: the client and sales executive (found by name
 * or created), the project, then its location.
 */
const useSaveProject = () => {
  const [listClients] =
    generatedV2.endpoints.v2ListClientsForAccount.useLazyQuery();
  const [listSalesExecs] =
    generatedV2.endpoints.v2ListSalesExecutivesForAccount.useLazyQuery();
  const [listLocations] =
    generatedV2.endpoints.v2ListProjectLocations.useLazyQuery();
  const [createClient] = useV2CreateClientMutation();
  const [createSalesExec] = useV2CreateSalesExecutiveMutation();
  const [createProject] = useV2CreateProjectMutation();
  const [updateProject] = useV2UpdateProjectMutation();
  const [createLocation] = useV2CreateProjectLocationMutation();
  const [updateLocation] = useV2UpdateProjectLocationMutation();
  const createdProjectId = useRef<string | null>(null);

  // Inactive records count too, so a deactivated client is reused, not duplicated.
  const findByName = async (
    list: (arg: any) => { unwrap: () => Promise<{ data?: ApiRecord[] }> },
    name: string
  ) => {
    const res = await list({ filter: { name, active: "true,false" } }).unwrap();
    return res.data?.find((r) => r.attributes?.name === name)?.id ?? null;
  };

  const resolveClient = async ({ id, name }: NewProjectValues["client"]) => {
    if (id) return id;
    try {
      const found = await findByName(listClients, name);
      if (found) return found;
      const res = await createClient({
        body: { data: { type: "clients", attributes: { name } } },
      } as any).unwrap();
      return String(res.data?.id);
    } catch (err) {
      throw new ProjectSaveError(
        "client",
        formatUnprocessibleResponse({ response: err }, "Client")
      );
    }
  };

  // A sales executive that can't be created doesn't block the project.
  const resolveSalesExec = async (
    salesExecutive: NewProjectValues["salesExecutive"]
  ): Promise<SalesExec> => {
    if (!salesExecutive) return { id: null, warning: null };
    if (salesExecutive.id) return { id: salesExecutive.id, warning: null };
    const { name } = salesExecutive;
    try {
      const found = await findByName(listSalesExecs, name);
      if (found) return { id: found, warning: null };
      const res = await createSalesExec({
        body: { data: { type: "sales-executives", attributes: { name } } },
      } as any).unwrap();
      return { id: String(res.data?.id), warning: null };
    } catch {
      return {
        id: null,
        warning: `${name} could not be added as the sales executive. Add them in Settings > Sales Executives.`,
      };
    }
  };

  // Creates the project, or updates it when given its id.
  const saveProject = async (values: NewProjectValues, projectId?: string) => {
    const clientId = await resolveClient(values.client);
    const salesExec = await resolveSalesExec(values.salesExecutive);
    const data = projectData(values, clientId, salesExec, projectId);
    try {
      const res = projectId
        ? await updateProject({ id: projectId, body: { data } } as any).unwrap()
        : await createProject({ body: { data } } as any).unwrap();
      return { projectId: String(res.data?.id), warning: salesExec.warning };
    } catch (err) {
      throw new ProjectSaveError(
        "project",
        formatUnprocessibleResponse({ response: err }, "Project")
      );
    }
  };

  // Updates the location when it has an id, creates it otherwise.
  const saveLocation = async (
    projectId: string,
    location: NewProjectLocation,
    locationId: string | null
  ) => {
    const data = locationBody(projectId, location, locationId) as any;
    try {
      if (locationId) {
        await updateLocation({ id: locationId, body: { data } }).unwrap();
      } else {
        await createLocation({ body: { data } }).unwrap();
      }
    } catch (err) {
      throw new ProjectSaveError(
        "location",
        locationSaveFailedMessage({ response: err })
      );
    }
  };

  /**
   * If the location save fails, the next create() retries only the location,
   * so a project is never created twice.
   */
  const create = async (values: NewProjectValues) => {
    let warning: string | null = null;
    if (!createdProjectId.current) {
      const saved = await saveProject(values);
      createdProjectId.current = saved.projectId;
      warning = saved.warning;
    }
    const projectId = createdProjectId.current;
    if (values.location) {
      // Project create can already have made a location (the API applies the
      // CRM opportunity's site); update it rather than create a duplicate.
      let locationId: string | null;
      try {
        const existing = await listLocations({
          filter: { project: projectId },
        }).unwrap();
        locationId = existing.data?.[0]?.id
          ? String(existing.data[0].id)
          : null;
      } catch (err) {
        throw new ProjectSaveError(
          "location",
          locationSaveFailedMessage({ response: err })
        );
      }
      await saveLocation(projectId, values.location, locationId);
    }
    return { projectId, warning };
  };

  // `locationId` is the saved location's, null to create one.
  const update = async (
    projectId: string,
    values: NewProjectValues,
    locationId: string | null
  ) => {
    const { warning } = await saveProject(values, projectId);
    if (values.location) {
      await saveLocation(projectId, values.location, locationId);
    }
    return { warning };
  };

  return { create, update };
};

export default useSaveProject;
